// Invoice Store

import { getField, updateField } from 'vuex-map-fields'
import { cloneDeep, map } from 'lodash'

const invoice = {
  invoice_nr: null,
  client_id: null,
  billingdate: null,
  paymenttime: 14,
  skonto: 0,
  tenant_id: null,
  invoicerange_id: null,
  extratext: null,
  invoice_products: [],
  discount: 0,
  discount_type: 'percent',
  shipping_type: 'hide',
  shippingdate: null,
  shippingenddate: null,
  tenant: {
    billing_handle: {},
  },
}

const state = {
  errors: {},
  invoice: cloneDeep(invoice),
  invoicePdf: null,
  invoicePresets: {},
  pending: {},
}

const getters = {
  getField,
}

const actions = {
  getInvoice({ commit, rootState }) {
    return new Promise((resolve, reject) => {
      const { id } = rootState.route.params

      commit('SET_PENDING', { pending: true, type: 'getInvoice' })

      this.api.item
        .getItem('invoices', id)
        .then(({ data }) => {
          commit('GET_INVOICE_SUCCESS', data.invoice)
          resolve()
        })
        .catch(error => {
          commit('GET_INVOICE_FAIL', error)
          reject(error)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'getInvoice' })
        })
    })
  },
  getInvoicePdf({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'getInvoicePdf' })

      this.api.invoice
        .getPdf(id)
        .then(({ data }) => {
          commit('GET_INVOICE_PDF_SUCCESS', data.invoice)
          resolve(data.invoice)
        })
        .catch(error => {
          commit('GET_INVOICE_PDF_FAIL', error)
          reject(error)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'getInvoicePdf' })
        })
    })
  },
  getInvoicePdfPreview({ commit, state }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'getInvoicePdfPreview' })

      this.api.invoice
        .getPdfPreview(state.invoice)
        .then(({ data }) => {
          commit('GET_INVOICE_PDF_SUCCESS', data.invoice)
          resolve()
        })
        .catch(error => {
          commit('GET_INVOICE_PDF_FAIL', error)
          reject(error)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'getInvoicePdfPreview' })
        })
    })
  },
  getEditInvoice({ commit, rootState }) {
    return new Promise((resolve, reject) => {
      const { id } = rootState.route.params

      commit('SET_PENDING', { pending: true, type: 'getEditInvoice' })

      this.api.item
        .getEditItem('invoices', id)
        .then(({ data }) => {
          const invoice = cloneDeep(data).invoice
          invoice.invoice_products = map(invoice.invoice_products, p => {
            if (p.artNr) {
              p.isDirty = true
            }
            return p
          })
          commit('GET_INVOICE_SUCCESS', invoice)
          commit('GET_INVOICE_PRESETS_SUCCESS', {
            invoiceranges: data.invoiceranges,
            tenants: data.tenants,
          })
          resolve()
        })
        .catch(error => {
          commit('GET_INVOICE_FAIL', error)
          reject(error)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'getEditInvoice' })
        })
    })
  },
  getInvoicePresets({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'getInvoicePresets' })

      this.api.item
        .getOptions('invoices')
        .then(({ data }) => {
          commit('GET_INVOICE_PRESETS_SUCCESS', data)
          resolve()
        })
        .catch(error => {
          commit('GET_INVOICE_PRESETS_FAIL', error)
          reject(error)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'getInvoicePresets' })
        })
    })
  },
  createInvoice({ commit, state }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'createInvoice' })

      delete state.invoice.tenant

      this.api.item
        .addItem('invoices', state.invoice)
        .then(({ data }) => {
          if (data && data.errors) {
            commit('ADD_INVOICE_FAIL', data.errors)
            return
          }
          commit('ADD_INVOICE_SUCCESS')
          resolve(data)
        })
        .catch(error => {
          commit('ADD_INVOICE_FAIL', error)
          reject(error)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'createInvoice' })
        })
    })
  },
  editInvoice({ commit, state }, onlyEdit = true) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'editInvoice' })

      this.api.item
        .editItem('invoices', state.invoice)
        .then(({ data }) => {
          if (data && data.errors) {
            commit('EDIT_INVOICE_FAIL', data.errors)
            return
          }

          if (onlyEdit) {
            commit('EDIT_INVOICE_SUCCESS')
          } else {
            const invoice = cloneDeep(data).invoice
            invoice.invoice_products = map(invoice.invoice_products, p => {
              if (p.artNr) {
                p.isDirty = true
              }
              return p
            })
            commit('EDIT_INVOICE_SUCCESS', invoice)
          }

          resolve(data)
        })
        .catch(error => {
          commit('EDIT_INVOICE_FAIL', error)
          reject(error)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'editInvoice' })
        })
    })
  },
  addInvoiceProduct({ commit }) {
    commit('ADD_INVOICE_PRODUCT')
  },
  removeInvoiceProduct({ commit }, index) {
    commit('REMOVE_INVOICE_PRODUCT', index)
  },
  clearInvoice({ commit }) {
    commit('CLEAR_INVOICE')
  },
}

const mutations = {
  SET_PENDING: (state, { pending, type = 'default' }) => {
    state.pending = { ...state.pending, [type]: pending }
  },
  CLEAR_INVOICE(state) {
    state.invoice = cloneDeep(invoice)
    state.invoicePdf = null
    state.invoicePresets = {}
    state.errors = {}
  },
  ADD_INVOICE_PRODUCT(state) {
    state.invoice.invoice_products.push({
      artNr: null,
      quantity: null,
      amount: null,
      title: null,
      description: null,
      tax: 19,
      discount: null,
      discount_type: null,
      isDirty: false,
      sort_nr: state.invoice.invoice_products.length + 1,
    })
  },
  UPDATE_ALL_INVOICE_PRODUCTS(state, products) {
    const allProducts = products.map((product, index) => ({
      ...product,
      sort_nr: index + 1,
    }))
    state.invoice.invoice_products = [...allProducts]
  },
  REMOVE_INVOICE_PRODUCT(state, index) {
    state.invoice.invoice_products.splice(index, 1)
    this.commit('invoice/UPDATE_ALL_INVOICE_PRODUCTS', state.invoice.invoice_products)
  },
  GET_INVOICE_SUCCESS(state, data) {
    state.errors = {}
    state.invoice = data
  },
  GET_INVOICE_FAIL(state, errors) {
    state.errors = errors
  },
  ADD_INVOICE_SUCCESS(state) {
    state.errors = {}
  },
  ADD_INVOICE_FAIL(state, errors) {
    state.errors = errors
  },
  GET_INVOICE_PRESETS_SUCCESS(state, data) {
    state.invoicePresets = data
    state.errors = {}
  },
  GET_INVOICE_PRESETS_FAIL(state, errors) {
    state.errors = errors
  },
  EDIT_INVOICE_SUCCESS(state, invoice = false) {
    state.errors = {}
    if (invoice) {
      state.invoice = invoice
    }
  },
  EDIT_INVOICE_FAIL(state, errors) {
    state.errors = errors
  },
  GET_INVOICE_PDF_SUCCESS(state, data) {
    state.errors = {}
    state.invoicePdf = data
  },
  GET_INVOICE_PDF_FAIL(state, errors) {
    state.errors = errors
  },
  updateField,
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
